import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
// @ts-ignore
import ApexCharts from 'apexcharts'
import {ApiService} from "../../services/api/api.service";
import {AuthenticationService} from "../../services/auth/authentication.service";
import {FormatService} from '../../services/format/format.service';

const PRIMARY = '#1761fd';
// neutral chart colors are read from the live tokens so dark mode flips them
function cssVar(name: string, fallback: string) {
  const v = getComputedStyle(document.body).getPropertyValue(name).trim();
  return v || fallback;
}
const MUTED = () => cssVar('--hatam-muted', '#7c8db5');
const LINE = () => cssVar('--hatam-line', '#e3ebf6');
const SURFACE = () => cssVar('--hatam-surface', '#ffffff');
// gender palette: two real categories + a deliberate neutral for "not filled"
const GENDER_COLORS = ['#1761fd', '#db4a68', '#94a7c9'];
const GENDER_LABELS = ['Laki-laki', 'Perempuan', 'Tidak diisi'];

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {

  isRange: number = 30; // 7 | 30 | 90 — drives the visits chart
  isUpdatedVw: string = '';

  // KPI tiles
  kpi: any = {
    user: '…', userDelta: null,
    visitsToday: '…', visitDelta: null,
    sahamqu: '…', sahamquTrx: '…', sahamquDelta: null, subsDelta: null,
    subs: '…', subsVoucher: '…', subsStore: '…',
  };

  // content card
  content: any = {audio: '…', feed: '…', video: '…', hafalan: '…'};

  // subscription bars
  bars: any = null; // {voucher: {value, pct}, store: {value, pct}, total, weeklyAvg}

  // gender legend rows
  genderRows: any = [];
  genderTotal: string = '…';

  isChartLoading: boolean = false;

  private visitChart: any = null;
  private donutChart: any = null;

  constructor(
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    this.loadSummary();
    this.loadVisits();
    this.loadGender();
    this.loadUsage();
  }

  // repaint every chart with the new token colors when the theme flips
  @HostListener('window:hatam-theme')
  onThemeChange() {
    this.loadVisits();
    this.loadGender();
    this.renderSubsPie(); // stroke gaps must pick up the new surface color
  }

  ngOnDestroy(): void {
    if (this.visitChart) { this.visitChart.destroy(); }
    if (this.donutChart) { this.donutChart.destroy(); }
    if (this.subsPie) { this.subsPie.destroy(); }
  }

  // ===== mini donut beside the subscription total (same palette as the bars card) =====
  private subsPie: any = null;
  private subsPieData: number[] | null = null;

  renderSubsPie() {
    if (this.subsPie) { this.subsPie.destroy(); this.subsPie = null; }
    const data = this.subsPieData;
    if (!data || !data.some(v => v > 0)) { return; }
    const options = {
      chart: {type: 'pie', width: 38, height: 38, sparkline: {enabled: true}, animations: {enabled: false}},
      series: data,
      labels: ['Voucher', 'Play Store', 'App Store'],
      colors: ['#1761fd', '#d89e30', '#db4a68'],
      stroke: {width: 2, colors: [SURFACE()]}, // 2px surface gaps between slices
      dataLabels: {enabled: false},
      legend: {show: false},
      // a tooltip cannot fit inside a 38px box — the dot legend already carries the numbers
      tooltip: {enabled: false},
    };
    this.subsPie = new ApexCharts(document.querySelector('#subsPie'), options);
    this.subsPie.render();
  }

  fmt(value: any) {
    return this.formatService.dotFormat(value || 0);
  }

  // percentage with an Indonesian decimal comma, one digit
  private pct(part: number, base: number) {
    if (!base) { return null; }
    return (part / base * 100).toFixed(1).replace('.', ',');
  }

  setRange(days: number) {
    if (this.isRange === days) { return; }
    this.isRange = days;
    this.loadVisits();
    this.loadUsage();
  }

  loadSummary() {
    try {
      this.apiService.methodGetSilent(`dashboard/summary`, this.authService.getToken()).subscribe((response) => {
        if (!(response && response.status == 200)) { return; }
        const d = response.data;

        // stamped when the data actually arrived, not when the page rendered
        const now = new Date();
        this.isUpdatedVw = now.toLocaleDateString('id-ID', {day: 'numeric', month: 'short', year: 'numeric'})
          + ', ' + now.toLocaleTimeString('id-ID', {hour: '2-digit', minute: '2-digit'});

        const prevUsers = (d.user || 0) - (d.user_new_30d || 0);
        const subsTotal = (d.user_voucher || 0) + (d.user_store || 0);
        this.kpi = {
          user: this.fmt(d.user),
          userDelta: prevUsers > 0 && d.user_new_30d > 0 ? this.pct(d.user_new_30d, prevUsers) : null,
          visitsToday: this.fmt(d.visits_today),
          visitDelta: d.visits_yesterday > 0
            ? this.pct((d.visits_today || 0) - d.visits_yesterday, d.visits_yesterday)
            : null,
          visitUp: (d.visits_today || 0) >= (d.visits_yesterday || 0),
          sahamqu: this.formatService.currency(d.sahamqu || 0),
          sahamquTrx: this.fmt(d.sahamqu_trx),
          // one-month windows compared head-to-head, same idea as today-vs-yesterday visits
          sahamquDelta: d.sahamqu_prev_30d > 0
            ? this.pct((d.sahamqu_30d || 0) - d.sahamqu_prev_30d, d.sahamqu_prev_30d)
            : null,
          sahamquUp: (d.sahamqu_30d || 0) >= (d.sahamqu_prev_30d || 0),
          subsDelta: d.subs_prev_30d > 0
            ? this.pct((d.subs_new_30d || 0) - d.subs_prev_30d, d.subs_prev_30d)
            : null,
          subsUp: (d.subs_new_30d || 0) >= (d.subs_prev_30d || 0),
          subs: this.fmt(subsTotal),
          subsVoucher: this.fmt(d.user_voucher),
          subsPlay: this.fmt(d.user_store_android),
          subsApp: this.fmt(d.user_store_ios),
        };

        this.content = {
          audio: this.fmt(d.audio),
          feed: this.fmt(d.teacher_feed),
          video: this.fmt(d.video),
          hafalan: this.fmt(d.hafalan),
        };

        this.subsPieData = [d.user_voucher || 0, d.user_store_android || 0, d.user_store_ios || 0];
        this.renderSubsPie();

        const total = (d.user_voucher || 0) + (d.user_store || 0);
        this.bars = {
          voucher: {value: this.fmt(d.user_voucher), pct: total ? (d.user_voucher / total * 100) : 0},
          play: {value: this.fmt(d.user_store_android), pct: total ? ((d.user_store_android || 0) / total * 100) : 0},
          appstore: {value: this.fmt(d.user_store_ios), pct: total ? ((d.user_store_ios || 0) / total * 100) : 0},
          total: this.fmt(total),
          weeklyAvg: this.fmt(Math.round((d.subs_new_30d || 0) / 4.3)),
        };
      });
    } catch (error) {
      console.log(error);
    }
  }

  // ===== app usage (request-header log): who uses which platform/version, where =====
  usage: any = null; // null = loading; {empty} when no new-build traffic yet

  loadUsage() {
    const range = this.isRange;
    this.apiService.methodGetSilent(`dashboard/usage/${range}`, this.authService.getToken()).subscribe((response) => {
      if (range !== this.isRange) { return; }
      if (!(response && response.status == 200)) { this.usage = {empty: true, failed: true}; return; }
      const d = response.data || {};
      const platformNames: any = {android: 'Android', ios: 'iOS', web: 'Web', tv: 'Android TV', '': 'Tidak diketahui'};
      const total = d.devices || 0;
      const bars = (rows: any[], label?: any) => (rows || []).map((r: any) => ({
        name: label ? (label[r.name] || r.name) : (r.name || 'Tidak diketahui'),
        value: this.fmt(r.devices),
        pct: total ? (r.devices / total * 100) : 0,
      }));
      this.usage = {
        empty: !total,
        devices: this.fmt(total),
        users: this.fmt(d.users || 0),
        groups: [
          {title: 'Platform', rows: bars(d.platforms, platformNames)},
          {title: 'Versi Aplikasi', rows: bars(d.versions)},
          {title: 'Provinsi', rows: bars(d.provinces)},
          {title: 'Perangkat', rows: bars(d.device_models)},
        ],
      };
    }, () => { if (range === this.isRange) { this.usage = {empty: true, failed: true}; } });
  }

  loadVisits() {
    const range = this.isRange; // guard: a stale response must never win a range race
    this.isChartLoading = true;
    try {
      this.apiService.methodGetSilent(`dashboard/daily-visit/${range}`, this.authService.getToken()).subscribe(async (response) => {
        if (range !== this.isRange) { return; } // the user already switched ranges
        this.isChartLoading = false;
        if (!(response && response.status == 200)) { return; }
        const data = response.data || [];
        if (this.visitChart) { this.visitChart.destroy(); this.visitChart = null; }
        const options = {
          chart: {
            type: 'area', height: 300, fontFamily: 'Poppins, sans-serif',
            toolbar: {show: false}, zoom: {enabled: false},
            animations: {speed: 350},
          },
          series: [{name: 'Pengunjung', data: data}],
          colors: [PRIMARY],
          stroke: {curve: 'smooth', width: 2},
          fill: {type: 'solid', opacity: 0.08},
          dataLabels: {enabled: false},
          markers: {size: 0, hover: {size: 5}},
          grid: {borderColor: LINE(), strokeDashArray: 0, padding: {left: 8, right: 8}},
          xaxis: {
            type: 'datetime',
            axisBorder: {show: false}, axisTicks: {show: false},
            // dates arrive as 'YYYY-MM-DD' strings; UTC mode shows them verbatim,
            // so no server/browser timezone combination can shift a label
            labels: {style: {colors: MUTED(), fontSize: '11px'}, datetimeUTC: true},
          },
          yaxis: {
            labels: {
              style: {colors: MUTED(), fontSize: '11px'},
              formatter: (v: number) => this.fmt(Math.round(v)),
            },
            tickAmount: 4,
          },
          tooltip: {
            x: {format: 'dd MMM yyyy'},
            y: {formatter: (v: number) => `${this.fmt(v)} pengunjung`},
          },
        };
        this.visitChart = new ApexCharts(document.querySelector('#visitChart'), options);
        await this.visitChart.render();
      }, () => { this.isChartLoading = false; });
    } catch (error) {
      this.isChartLoading = false;
      console.log(error);
    }
  }

  loadGender() {
    try {
      this.apiService.methodGetSilent(`dashboard/gender`, this.authService.getToken()).subscribe(async (response) => {
        if (!(response && response.status == 200)) { return; }
        const data = (response.data && response.data.length) ? response.data.map((v: any) => v || 0) : [0, 0, 0];
        const total = data.reduce((s: number, v: number) => s + v, 0);
        this.genderTotal = this.fmt(total);
        this.genderRows = data.map((v: number, i: number) => ({
          label: GENDER_LABELS[i],
          color: GENDER_COLORS[i],
          value: this.fmt(v),
          pct: total ? `${this.pct(v, total)}%` : '—',
        }));
        if (this.donutChart) { this.donutChart.destroy(); this.donutChart = null; }
        const options = {
          chart: {type: 'donut', height: 190, fontFamily: 'inherit', animations: {speed: 350}},
          series: total ? data : [1],
          labels: total ? GENDER_LABELS : ['Belum ada data'],
          colors: total ? GENDER_COLORS : [LINE()],
          dataLabels: {enabled: false},
          legend: {show: false}, // the legend is our own HTML rows, with counts & percentages
          stroke: {width: 2, colors: [SURFACE()]}, // 2px surface gaps between slices
          plotOptions: {pie: {donut: {size: '76%'}, expandOnClick: false}},
          tooltip: {
            enabled: !!total,
            y: {formatter: (v: number) => `${this.fmt(v)} pengguna`},
          },
          states: {active: {filter: {type: 'none'}}},
        };
        this.donutChart = new ApexCharts(document.querySelector('#genderDonut'), options);
        await this.donutChart.render();
      });
    } catch (error) {
      console.log(error);
    }
  }
}
